import { useEffect, useState } from "react";
import { Upload, Music4, Mic2, Save, Wallet, Trash2, CheckCircle2, XCircle, Clock3, User, Award } from "lucide-react";
import { http, fmtDate, fmtRupee, errMsg } from "../api";
import AudioPlayer from "../components/AudioPlayer";
import ArtistTelegram from "./ArtistTelegram";
import ArtistPitchLinks from "./ArtistPitchLinks";

const MAX_SAMPLE_BYTES = 30 * 1024 * 1024;

function ProfileTab({ config }) {
  const [artist, setArtist] = useState(null);
  const [form, setForm] = useState({});
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { http.get("/artists/me").then(r => { setArtist(r.data); setForm(r.data); }); }, []);
  if (!artist) return <div className="empty">Loading profile...</div>;

  const toggleArr = (key, v) => {
    const arr = new Set(form[key] || []);
    arr.has(v) ? arr.delete(v) : arr.add(v);
    setForm({ ...form, [key]: Array.from(arr) });
  };
  const toggleChar = v => {
    const arr = new Set(form.characteristics || []);
    if (arr.has(v)) arr.delete(v);
    else if (arr.size < 4) arr.add(v);
    else return;
    setForm({ ...form, characteristics: Array.from(arr) });
  };
  const save = async () => {
    setBusy(true); setNotice("");
    try {
      const { data } = await http.patch("/artists/me", form);
      setArtist(data); setForm(data);
      setNotice("Profile updated successfully");
    } catch (e) { setNotice(errMsg(e)); } finally { setBusy(false); }
  };

  const chars = form.characteristics || [];
  return (
    <div className="form-card" data-testid="artist-profile-form">
      <h2>Artist Profile</h2>
      <p>Keep your profile current. Admin reviews changes and updates marketplace metadata.</p>

      <div className="stats" style={{ marginBottom: 20 }}>
        <div className="stat-card"><span>Voice ID</span><strong style={{ fontSize: 18 }}>{artist.public_id}</strong><small>Public marketplace name</small></div>
        <div className={`stat-card ${artist.profile_status === "APPROVED" ? "success" : artist.profile_status === "REJECTED" ? "danger" : "warn"}`}>
          <span>Profile status</span><strong style={{ fontSize: 16 }}>{artist.profile_status}</strong>
        </div>
        <div className="stat-card"><span>Voice age (auto)</span><strong style={{ fontSize: 16 }}>{artist.voice_age}</strong><small>Admin can override</small></div>
      </div>

      <div className="field-row">
        <div>
          <label>Gender</label>
          <select value={form.gender || ""} onChange={e => setForm({ ...form, gender: e.target.value })}>
            <option value="">Select</option>
            <option>Male</option><option>Female</option><option>Non-binary</option><option>Prefer not to say</option>
          </select>
        </div>
        <div>
          <label>Phone</label>
          <input value={form.phone || ""} onChange={e => setForm({ ...form, phone: e.target.value })} />
        </div>
      </div>

      <div className="field-row">
        <div><label>State</label><input value={form.state || ""} onChange={e => setForm({ ...form, state: e.target.value })} /></div>
        <div><label>City</label><input value={form.city || ""} onChange={e => setForm({ ...form, city: e.target.value })} /></div>
      </div>

      <div className="field-row">
        <div>
          <label>Default profile pay (₹ per finished hour)</label>
          <input data-testid="artist-base-rate-input" type="number" value={form.base_rate || 0} onChange={e => setForm({ ...form, base_rate: parseFloat(e.target.value) || 0 })} />
          <div className="chars-counter">Each voice sample has its own pay and client price.</div>
        </div>
        <div>
          <label>Years of voice-over experience</label>
          <input type="number" value={form.experience_years || 0} onChange={e => setForm({ ...form, experience_years: parseFloat(e.target.value) || 0 })} />
        </div>
      </div>

      <div className="field-row">
        <div>
          <label>Availability</label>
          <select value={form.availability || "Available"} onChange={e => setForm({ ...form, availability: e.target.value })}>
            <option>Available</option><option>Limited availability</option><option>Unavailable</option>
          </select>
        </div>
        <div>
          <label>Home studio</label>
          <select value={form.home_studio ? "yes" : "no"} onChange={e => setForm({ ...form, home_studio: e.target.value === "yes" })}>
            <option value="yes">Yes, professional home setup</option><option value="no">No home studio</option>
          </select>
        </div>
      </div>

      <div>
        <label>Microphone / recording gear</label>
        <input value={form.microphone || ""} onChange={e => setForm({ ...form, microphone: e.target.value })} placeholder="e.g., Rode NT1, Focusrite Scarlett" />
      </div>

      <div>
        <label>Accent / dialect (optional)</label>
        <input value={form.accent || ""} onChange={e => setForm({ ...form, accent: e.target.value })} placeholder="Neutral, Punjabi-inflected, etc." />
      </div>

      <div>
        <label>Portfolio / demo link (optional)</label>
        <input value={form.portfolio_link || ""} onChange={e => setForm({ ...form, portfolio_link: e.target.value })} placeholder="https://" />
      </div>

      <div>
        <label>Preferred languages ({(form.languages || []).length} selected)</label>
        <div className="chip-row" data-testid="artist-languages-chips">
          {config.languages.map(l => (
            <button key={l} type="button" className={`chip ${(form.languages || []).includes(l) ? "active" : ""}`} onClick={() => toggleArr("languages", l)}>{l}</button>
          ))}
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <label>Use cases you specialise in (tick up to 4)</label>
        <div className="chip-row" data-testid="artist-categories-chips">
          {config.categories.map(c => (
            <button key={c} type="button" className={`chip ${(form.categories || []).includes(c) ? "active" : ""} ${!(form.categories || []).includes(c) && (form.categories || []).length >= 4 ? "disabled" : ""}`} onClick={() => { if ((form.categories || []).includes(c) || (form.categories || []).length < 4) toggleArr("categories", c); }}>{c}</button>
          ))}
        </div>
        <div className={`chars-counter ${(form.categories || []).length >= 4 ? "max" : ""}`}>{(form.categories || []).length}/4 selected</div>
      </div>

      <div style={{ marginTop: 14 }}>
        <label>Voice characteristics (max 4)</label>
        <div className="chip-row" data-testid="artist-chars-chips">
          {config.characteristics.map(c => (
            <button key={c} type="button" className={`chip ${chars.includes(c) ? "active" : ""} ${!chars.includes(c) && chars.length >= 4 ? "disabled" : ""}`} onClick={() => toggleChar(c)}>{c}</button>
          ))}
        </div>
        <div className={`chars-counter ${chars.length >= 4 ? "max" : ""}`}>{chars.length}/4 selected</div>
      </div>

      {notice && <div className={`notice ${notice.includes("success") ? "success" : "error"}`} style={{ marginTop: 14 }}>{notice}</div>}
      <button className="primary" onClick={save} disabled={busy} data-testid="save-profile-btn" style={{ marginTop: 14 }}>
        {busy ? "Saving..." : <><Save size={15} /> Save profile</>}
      </button>
    </div>
  );
}

function SamplesTab({ config }) {
  const [samples, setSamples] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const blank = { title: "", language: "Hindi", categories: [], characteristics: [], artist_rate: "", description: "", accent: "", ai_voice: false };
  const [form, setForm] = useState(blank);
  const [file, setFile] = useState(null);
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState("");

  const load = () => http.get("/samples/mine").then(r => setSamples(r.data));
  useEffect(() => { load(); }, []);

  const toggleChar = v => {
    const arr = new Set(form.characteristics);
    if (arr.has(v)) arr.delete(v);
    else if (arr.size < 4) arr.add(v);
    else return;
    setForm({ ...form, characteristics: Array.from(arr) });
  };
  const toggleCat = v => {
    const arr = new Set(form.categories);
    if (arr.has(v)) arr.delete(v);
    else if (arr.size < 4) arr.add(v);
    else return;
    setForm({ ...form, categories: Array.from(arr) });
  };
  const startUpload = async () => {
    if (!file) { setNotice("Please select an audio file"); return; }
    if (file.size > MAX_SAMPLE_BYTES) { setNotice("Audio sample must be smaller than 30 MB"); return; }
    if (form.categories.length === 0) { setNotice("Tick at least one use case"); return; }
    if (Number(form.artist_rate) <= 0) { setNotice("Add your pay per finished hour for this sample"); return; }
    setBusy(true); setNotice(""); setUploadError(""); setUploadProgress(0);
    const fd = new FormData();
    fd.append("title", form.title); fd.append("language", form.language); fd.append("artist_rate", form.artist_rate); fd.append("description", form.description); fd.append("accent", form.accent);
    fd.append("categories", form.categories.join("|")); fd.append("characteristics", form.characteristics.join(",")); fd.append("ai_voice", String(form.ai_voice));
    fd.append("file", file);
    try {
      await http.post("/samples", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: event => {
          if (event.total) setUploadProgress(Math.min(99, Math.round((event.loaded * 100) / event.total)));
        },
      });
      setUploadProgress(100);
      setNotice("Sample uploaded. Awaiting admin review.");
      setForm(blank);
      setFile(null); setShowForm(false); load();
    } catch (e) { setUploadError(errMsg(e)); } finally { setBusy(false); }
  };
  const upload = e => { e.preventDefault(); startUpload(); };
  const archive = async id => {
    if (!confirm("Archive this sample?")) return;
    await http.delete(`/samples/${id}`); load();
  };
  const tone = s => s === "APPROVED" ? "success" : s === "REJECTED" ? "danger" : s === "ARCHIVED" ? "info" : "warning";

  return (
    <>
      <div className="page-head">
        <div><h2>My Voice Samples</h2><p>Every sample is reviewed by our team before appearing on the marketplace.</p></div>
        <button className="primary" onClick={() => setShowForm(v => !v)} data-testid="toggle-upload-form"><Upload size={15} /> {showForm ? "Cancel" : "Upload new sample"}</button>
      </div>

      {showForm && (
        <div className="form-card" style={{ marginBottom: 16 }}>
          <div className="form-notice">
            <strong>Upload your best voice sample.</strong> Your sample will be reviewed by our team and will be one of the primary factors used to match your voice with suitable projects. Please submit clean, high-quality audio recorded in your professional setup. Avoid background noise, clipping, or poor microphone quality.
          </div>
          <form onSubmit={upload}>
            <div className="field-row">
              <div>
                <label>Sample title</label>
                <input data-testid="sample-title-input" required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Hindi audiobook — Chapter 1" />
              </div>
              <label className={`checkbox-label ${form.ai_voice ? "checked" : ""}`} style={{ alignSelf: "end" }} data-testid="sample-ai-voice">
                <input type="checkbox" checked={form.ai_voice} onChange={e => setForm({ ...form, ai_voice: e.target.checked })} /> This is an AI-generated voice
              </label>
            </div>
            <div>
              <label>My pay per finished hour (₹)</label>
              <input type="number" min="1" step="1" required value={form.artist_rate} onChange={e => setForm({ ...form, artist_rate: e.target.value })} placeholder="e.g. 1200" data-testid="sample-artist-rate-input" />
              <div className="chars-counter">Client price is calculated as your pay + 20% platform margin.</div>
            </div>
            <div className="field-row">
              <div>
                <label>Primary language</label>
                <select data-testid="sample-language-input" value={form.language} onChange={e => setForm({ ...form, language: e.target.value })}>
                  {config.languages.map(l => <option key={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label>Accent / dialect (optional)</label>
                <input value={form.accent} onChange={e => setForm({ ...form, accent: e.target.value })} placeholder="Neutral" />
              </div>
            </div>
            <div>
              <label>Use cases for this sample (tick up to 4)</label>
              <div className="chip-row" data-testid="sample-categories-chips">
                {config.categories.map(c => (
                  <button key={c} type="button" className={`chip ${form.categories.includes(c) ? "active" : ""} ${!form.categories.includes(c) && form.categories.length >= 4 ? "disabled" : ""}`} onClick={() => toggleCat(c)} data-testid={`sample-cat-${c}`}>{c}</button>
                ))}
              </div>
              <div className={`chars-counter ${form.categories.length >= 4 ? "max" : ""}`}>{form.categories.length}/4 selected</div>
            </div>
            <div>
              <label>Sample description (optional)</label>
              <textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Notes for the reviewer..." />
            </div>
            <div>
              <label>Voice characteristics for this sample (max 4)</label>
              <div className="chip-row" data-testid="sample-chars-chips">
                {config.characteristics.map(c => (
                  <button key={c} type="button" className={`chip ${form.characteristics.includes(c) ? "active" : ""} ${!form.characteristics.includes(c) && form.characteristics.length >= 4 ? "disabled" : ""}`} onClick={() => toggleChar(c)}>{c}</button>
                ))}
              </div>
              <div className={`chars-counter ${form.characteristics.length >= 4 ? "max" : ""}`}>{form.characteristics.length}/4 selected</div>
            </div>
            <div>
              <label>Audio file (MP3, WAV or M4A · max 30 MB)</label>
              <label className="dropzone" data-testid="sample-dropzone">
                <input type="file" accept="audio/*" onChange={e => {
                  const selected = e.target.files[0];
                  if (selected?.size > MAX_SAMPLE_BYTES) { setFile(null); setNotice("Audio sample must be smaller than 30 MB"); e.target.value = ""; return; }
                  setUploadError(""); setUploadProgress(0);
                  setFile(selected);
                }} data-testid="sample-file-input" />
                <div className="dropzone-icon"><Upload size={20} /></div>
                <p>{file ? file.name : "Click to select audio"}</p>
                <small>{file ? `${(file.size / 1048576).toFixed(2)} MB` : "MP3, WAV, or M4A · up to 30 MB"}</small>
              </label>
            </div>
            {busy && <div className="upload-status" data-testid="sample-upload-progress"><div className="upload-status-head"><span>{uploadProgress < 99 ? `Uploading ${uploadProgress}%` : "Finalising secure upload…"}</span><strong>{uploadProgress}%</strong></div><div className="upload-progress-track"><div className="upload-progress-fill" style={{ width: `${uploadProgress}%` }} /></div></div>}
            {notice && <div className={`notice ${notice.includes("uploaded") ? "success" : "error"}`}>{notice}</div>}
            {uploadError && <div className="notice error" data-testid="sample-upload-error"><strong>Upload failed.</strong> {uploadError}<button type="button" className="ghost" onClick={startUpload} data-testid="sample-upload-retry">Retry upload</button></div>}
            <button className="primary" type="submit" disabled={busy} data-testid="sample-submit-btn">
              {busy ? "Uploading..." : <><Upload size={15} /> Submit for review</>}
            </button>
          </form>
        </div>
      )}

      {notice && !showForm && <div className="notice success">{notice}</div>}

      <div className="list-rows">
        {samples.map(s => (
          <div key={s.id} className="list-row" data-testid={`my-sample-${s.id}`}>
            <div className="list-row-info">
              <h3>{s.title}</h3>
              <p>{s.language} · {(s.categories || [s.category]).join(", ")} · Uploaded {fmtDate(s.created_at)}</p>
              {s.artist_base_rate > 0 && <p style={{ marginTop: 4 }}>Your pay: {fmtRupee(s.artist_base_rate)} / finished hour</p>}
              <div className="tag-row" style={{ marginTop: 6 }}>{s.ai_voice && <span className="badge ai">AI voice</span>}{s.characteristics?.map(c => <span className="badge" key={c}>{c}</span>)}</div>
              {s.admin_note && s.status === "REJECTED" && <div className="notice error" style={{ marginTop: 8, marginBottom: 0 }}>Reason: {s.admin_note}</div>}
            </div>
            <div className="list-row-actions">
              {s.status === "APPROVED" && <AudioPlayer id={s.id} compact />}
              <span className={`badge ${tone(s.status)}`}>{s.status}</span>
              {s.status !== "ARCHIVED" && <button className="pill-action neutral" onClick={() => archive(s.id)} data-testid={`archive-sample-${s.id}`}><Trash2 size={13} /></button>}
            </div>
          </div>
        ))}
        {samples.length === 0 && (
          <div className="empty" data-testid="no-samples">
            <Music4 size={30} />
            <div style={{ fontWeight: 600, marginBottom: 4 }}>No samples uploaded yet</div>
            <div style={{ fontSize: 13 }}>Upload your best voice sample to get discovered.</div>
          </div>
        )}
      </div>
    </>
  );
}

function PaymentTab() {
  const [payment, setPayment] = useState({ account_holder: "", bank_name: "", account_number: "", ifsc: "", upi_id: "" });
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { http.get("/artists/me/payment").then(r => { if (r.data && r.data.account_holder) setPayment(r.data); }); }, []);
  const save = async e => {
    e.preventDefault(); setBusy(true); setNotice("");
    try { await http.post("/artists/me/payment", payment); setNotice("Payment details saved. Only admin can view these."); }
    catch (e) { setNotice(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="form-card" data-testid="payment-form">
      <h2>Payment details</h2>
      <p>These details are private — visible only to admin for artist payouts. They are never shown to clients.</p>
      <form onSubmit={save}>
        <div>
          <label>Account holder name</label>
          <input required value={payment.account_holder} onChange={e => setPayment({ ...payment, account_holder: e.target.value })} />
        </div>
        <div className="field-row">
          <div><label>Bank name</label><input required value={payment.bank_name} onChange={e => setPayment({ ...payment, bank_name: e.target.value })} /></div>
          <div><label>IFSC code</label><input required value={payment.ifsc} onChange={e => setPayment({ ...payment, ifsc: e.target.value })} /></div>
        </div>
        <div>
          <label>Account number</label>
          <input required value={payment.account_number} onChange={e => setPayment({ ...payment, account_number: e.target.value })} />
        </div>
        <div>
          <label>UPI ID (optional)</label>
          <input value={payment.upi_id || ""} onChange={e => setPayment({ ...payment, upi_id: e.target.value })} placeholder="you@upi" />
        </div>
        {notice && <div className={`notice ${notice.includes("saved") ? "success" : "error"}`}>{notice}</div>}
        <button className="primary" type="submit" disabled={busy} data-testid="save-payment-btn"><Wallet size={15} /> Save payment details</button>
      </form>
    </div>
  );
}

function Overview() {
  const [artist, setArtist] = useState(null);
  const [samples, setSamples] = useState([]);
  const [orders, setOrders] = useState([]);
  useEffect(() => {
    http.get("/artists/me").then(r => setArtist(r.data));
    http.get("/samples/mine").then(r => setSamples(r.data));
    http.get("/orders").then(r => setOrders(r.data));
  }, []);
  if (!artist) return <div className="empty">Loading...</div>;
  const stats = [
    { label: "Approved samples", value: samples.filter(s => s.status === "APPROVED").length, tone: "success" },
    { label: "Pending review", value: samples.filter(s => s.status === "PENDING").length, tone: "warn" },
    { label: "Active orders", value: orders.filter(o => ["PENDING_ARTIST_ACKNOWLEDGEMENT", "IN_PROGRESS", "REVISION_REQUESTED"].includes(o.status)).length, tone: "" },
    { label: "Completed orders", value: orders.filter(o => o.status === "COMPLETED").length, tone: "success" },
  ];
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Welcome back, {artist.name}</h1>
          <p>Voice ID <strong>{artist.public_id}</strong> · Status <span className={`badge ${artist.profile_status === "APPROVED" ? "success" : "warning"}`}>{artist.profile_status}</span></p>
        </div>
      </div>
      <div className="stats">
        {stats.map(s => (
          <div key={s.label} className={`stat-card ${s.tone}`}>
            <span>{s.label}</span><strong>{s.value}</strong>
            <small>Live workspace data</small>
          </div>
        ))}
      </div>
      <div className="card">
        <div className="card-head"><h2><Award size={16} style={{ display: "inline", marginRight: 6 }} />Quick tips</h2></div>
        <ul style={{ margin: 0, paddingLeft: 18, color: "var(--ink-2)", fontSize: 13.5, lineHeight: 1.8 }}>
          <li>Upload at least one sample per language you offer — approvals are per sample.</li>
          <li>Keep your rate honest. Admin adds a platform margin for the marketplace price.</li>
          <li>Only accept orders when you can hit the deadline. Repeated revision requests hurt reliability metrics.</li>
          <li>Save your payment details early so payouts are fast after order completion.</li>
        </ul>
      </div>
    </>
  );
}

export default function ArtistDashboard({ page }) {
  const [config, setConfig] = useState({ languages: [], categories: [], characteristics: [] });
  useEffect(() => { http.get("/config").then(r => setConfig(r.data)); }, []);
  if (page === "dashboard") return <Overview />;
  if (page === "profile") return <ProfileTab config={config} />;
  if (page === "samples") return <SamplesTab config={config} />;
  if (page === "pitch-links") return <ArtistPitchLinks config={config} />;
  if (page === "payment") return <PaymentTab />;
  if (page === "telegram") return <ArtistTelegram />;
  return null;
}
